import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, ChevronLeft, ChevronRight, Plus, Filter, Calendar as CalendarIcon,
  Armchair, ListTodo, SlidersHorizontal,
} from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { buildChairBoard } from "@/lib/chairBoard";
import { getChairCapabilityMeta, normalizeChairCapabilities } from "@/lib/chairCapabilities";
import { buildAppointmentProcedureLines } from "@/lib/calendarAppointmentLines";
import {
  isBlockingAppointmentStatus,
  normalizeAppointmentStatus,
} from "@/lib/appointmentStatus";
import {
  CLINIC_TIME_ZONE,
  addClinicDays,
  clinicDateAt,
  clinicDateAtMinutes,
  clinicDateStringFromInstant,
  clinicDayIndex,
  clinicDayRange,
  clinicTimePartsFromInstant,
  clinicTodayDateString,
} from "@/lib/clinicTime";
import {
  CALENDAR_GRID_ZOOM_OPTIONS,
  DEFAULT_CALENDAR_GRID_ZOOM_MINUTES,
  MIN_SLOT_INTERVAL_MINUTES,
  defaultDurationMinutes,
  normalizeCalendarGridZoomMinutes,
  normalizeSlotIntervalMinutes,
} from "@/lib/calendarGranularity";
import { formatDoctorName } from "@/lib/doctorName";
import MiniMonthPicker from "@/components/calendar/MiniMonthPicker";
import SidePanel from "@/components/calendar/SidePanel";
import NewAppointmentPanel from "@/components/calendar/NewAppointmentPanel";
import AppointmentDetailPanel from "@/components/calendar/AppointmentDetailPanel";
import CalendarTasksPanel from "@/components/calendar/CalendarTasksPanel";
import { useFeatures } from "@/context/FeaturesContext";
import { useBranch } from "@/context/BranchContext";

// =====================================================================
// Calendar — horizontal day layout per Cameron's wireframe.
//
//   ┌──────────┬─────────────────────────────┬──────────────┐
//   │ Date     │ Time | Chair 1 | Chair 2 …  │ Slide-in     │
//   │ picker   │ 8:00 |         |            │ panel:       │
//   │ + today  │ 8:30 |  appt   |            │ - new appt   │
//   │ totals   │ 9:00 |         |  appt      │ - appt detail│
//   │          │ ...  │         │            │              │
//   └──────────┴─────────────────────────────┴──────────────┘
//
// Slide-in panel replaces the centered dialogs we shipped first.
// Clicking a different slot while the panel is open updates the
// panel in place (no flicker, no close-and-reopen).
// =====================================================================

// Minimum visual row height. Coarser grid zooms scale this up so short
// appointments remain legible inside an hour-sized row.
const SLOT_PX = 28;
const GRID_HOUR_TARGET_PX = 120;
const MIN_APPOINTMENT_CHIP_PX = 18;

// Fallbacks if settings haven't loaded yet (or for a brand-new clinic
// before defaults are seeded).
const FALLBACK_OPEN_MIN  = 8 * 60;   // 08:00
const FALLBACK_CLOSE_MIN = 19 * 60;  // 19:00
const FALLBACK_SLOT_MIN  = MIN_SLOT_INTERVAL_MINUTES;
const TUTORIAL_APPOINTMENT_MINUTES = MIN_SLOT_INTERVAL_MINUTES * 6;

const DAY_KEYS = ["sunday","monday","tuesday","wednesday","thursday","friday","saturday"];

const HIDDEN_CALENDAR_STATUSES = new Set(["Cancelled", "Rescheduled"]);

const STATUS_META = {
  Scheduled: {
    label: "Scheduled",
    eventClass: "bg-sky-50 text-sky-950 border-sky-200 hover:border-sky-300",
    pillClass: "bg-sky-100 text-sky-800",
    accentClass: "bg-sky-500",
    dotClass: "bg-sky-500",
  },
  Confirmed: {
    label: "Confirmed",
    eventClass: "bg-emerald-50 text-emerald-950 border-emerald-200 hover:border-emerald-300",
    pillClass: "bg-emerald-100 text-emerald-800",
    accentClass: "bg-emerald-500",
    dotClass: "bg-emerald-500",
  },
  "Checked In": {
    label: "Checked in",
    eventClass: "bg-amber-50 text-amber-950 border-amber-200 hover:border-amber-300",
    pillClass: "bg-amber-100 text-amber-800",
    accentClass: "bg-amber-500",
    dotClass: "bg-amber-500",
  },
  Completed: {
    label: "Done",
    eventClass: "bg-slate-50 text-slate-800 border-slate-200 hover:border-slate-300",
    pillClass: "bg-slate-200 text-slate-700",
    accentClass: "bg-slate-400",
    dotClass: "bg-slate-400",
  },
  Pencil: {
    label: "Pencil",
    eventClass: "bg-white text-slate-700 border-slate-300 border-dashed bg-[repeating-linear-gradient(45deg,_transparent,_transparent_4px,_rgba(148,163,184,0.12)_4px,_rgba(148,163,184,0.12)_8px)]",
    pillClass: "bg-slate-700 text-white",
    accentClass: "bg-slate-400",
    dotClass: "bg-slate-400",
  },
  "No Show": {
    label: "No show",
    eventClass: "bg-rose-50 text-rose-950 border-rose-200 hover:border-rose-300",
    pillClass: "bg-rose-100 text-rose-800",
    accentClass: "bg-rose-500",
    dotClass: "bg-rose-500",
  },
  Cancelled: {
    label: "Cancelled",
    eventClass: "bg-slate-50 text-slate-500 border-slate-200 line-through",
    pillClass: "bg-slate-100 text-slate-500",
    accentClass: "bg-slate-300",
    dotClass: "bg-slate-300",
  },
  Rescheduled: {
    label: "Rescheduled",
    eventClass: "bg-slate-50 text-slate-500 border-slate-200",
    pillClass: "bg-slate-100 text-slate-500",
    accentClass: "bg-slate-300",
    dotClass: "bg-slate-300",
  },
};

export function isBlockingCalendarStatus(status) {
  return isBlockingAppointmentStatus(status);
}

export function isVisibleCalendarStatus(status) {
  return !HIDDEN_CALENDAR_STATUSES.has(normalizeAppointmentStatus(status));
}

export default function Calendar() {
  const { access } = useFeatures();
  const { activeBranch, activeBranchId, loading: branchLoading } = useBranch();
  const [searchParams] = useSearchParams();
  const [day,        setDay]        = useState(() => startOfClinicDay(new Date()));
  const [doctorId,   setDoctorId]   = useState("");
  const [chairId,    setChairId]    = useState("");
  const [appts,      setAppts]      = useState([]);
  const [doctors,    setDoctors]    = useState([]);
  const [chairs,     setChairs]     = useState([]);
  const [settings,   setSettings]   = useState(null);
  const [gridZoomMinutes, setGridZoomMinutes] = useState(DEFAULT_CALENDAR_GRID_ZOOM_MINUTES);
  const [taskCount, setTaskCount] = useState(0);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [appointmentsRefreshKey, setAppointmentsRefreshKey] = useState(0);
  const loadSeqRef = useRef(0);

  // Side-panel state. mode:
  //   null            — closed
  //   { kind:'new', start, end, chairId? } — book a new appointment
  //   { kind:'detail', appointment }       — view + transition existing
  //   { kind:'tasks' }                     — review operational work on demand
  const [panel, setPanel] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const schedulingReadOnly = access?.mode === "read_only";
  const chairsideTutorialDemo =
    searchParams.get("tutorial") === "dental-chart"
    && searchParams.get("demo") === "1"
    && searchParams.get("handoff") === "chairside-checkin";
  const bookingTutorialDemo =
    searchParams.get("tutorial") === "booking-calendar"
    && searchParams.get("demo") === "1";

  const reload = useCallback(async () => {
    if (chairsideTutorialDemo || bookingTutorialDemo) {
      loadSeqRef.current += 1;
      const demo = buildChairsideTutorialDemoData(day);
      setLoading(false);
      setError(null);
      setAppts(bookingTutorialDemo ? [] : demo.appointments);
      setDoctors(demo.doctors);
      setChairs(demo.chairs);
      setSettings(demo.settings);
      return;
    }

    if (!activeBranchId) {
      loadSeqRef.current += 1;
      setLoading(Boolean(branchLoading));
      setError(branchLoading ? null : "No active branch selected.");
      setAppts([]);
      setDoctors([]);
      setChairs([]);
      return;
    }

    const loadSeq = loadSeqRef.current + 1;
    loadSeqRef.current = loadSeq;
    const branchScopeId = activeBranchId;

    setLoading(true); setError(null);
    const range = clinicDayRange(clinicDateStringFromInstant(day));
    const [aptRes, riskRes, docRes, chairRes, settingsRes, preferencesRes] = await Promise.all([
      supabase.rpc("list_appointments", {
        p_from:      range.from.toISOString(),
        p_to:        range.to.toISOString(),
        p_doctor_id: doctorId || null,
        p_chair_id:  chairId  || null,
        p_branch_id: branchScopeId,
      }),
      supabase.rpc("list_calendar_ops_risks", {
        p_from:      range.from.toISOString(),
        p_to:        range.to.toISOString(),
        p_doctor_id: doctorId || null,
        p_chair_id:  chairId  || null,
        p_branch_id: branchScopeId,
      }),
      supabase.rpc("list_clinic_doctor_records", {
        p_branch_id: branchScopeId,
      }),
      supabase.rpc("list_clinic_chairs", {
        p_branch_id: branchScopeId,
      }),
      supabase.rpc("get_appointment_settings"),
      supabase.rpc("get_calendar_user_preferences"),
    ]);
    if (loadSeq !== loadSeqRef.current) return;
    setLoading(false);
    if (aptRes.error)      { setError(aptRes.error.message ?? "Could not load appointments."); return; }
    setAppts(attachCalendarOpsRisks(aptRes.data ?? [], riskRes.data));
    if (!docRes.error)      setDoctors(docRes.data ?? []);
    if (!chairRes.error)    setChairs(chairRes.data ?? []);
    if (!settingsRes.error) setSettings(settingsRes.data ?? null);
    if (!preferencesRes.error) {
      setGridZoomMinutes(normalizeCalendarGridZoomMinutes(preferencesRes.data?.calendar_grid_zoom_minutes));
    }
  }, [activeBranchId, bookingTutorialDemo, branchLoading, chairId, chairsideTutorialDemo, day, doctorId]);
  useEffect(() => { reload(); }, [reload]);

  const reloadTaskCount = useCallback(async () => {
    if (chairsideTutorialDemo || bookingTutorialDemo) {
      setTaskCount(0);
      return;
    }
    if (!activeBranchId) {
      setTaskCount(0);
      return;
    }
    const [{ data: opsData }, { data: requestRows }] = await Promise.all([
      supabase.rpc("list_calendar_ops_queue", { p_branch_id: activeBranchId }),
      supabase.rpc("list_patient_booking_requests", { p_status: "open", p_branch_id: activeBranchId }),
    ]);
    const opsCount = Number(opsData?.metrics?.total || 0);
    const requestCount = Array.isArray(requestRows) ? requestRows.length : 0;
    setTaskCount(opsCount + requestCount);
  }, [activeBranchId, bookingTutorialDemo, chairsideTutorialDemo]);
  useEffect(() => { reloadTaskCount(); }, [reloadTaskCount]);

  const handleAppointmentsChanged = useCallback(() => {
    setAppointmentsRefreshKey((key) => key + 1);
    reload();
  }, [reload]);
  function handleTasksChanged() {
    handleAppointmentsChanged();
    reloadTaskCount();
  }

  const columns = useMemo(() => {
    if (chairId) {
      const c = chairs.find((x) => x.id === chairId);
      return c ? [c] : [];
    }
    return chairs;
  }, [chairs, chairId]);
  const needsChairSetup = !chairsideTutorialDemo && !bookingTutorialDemo && !loading && chairs.length === 0;

  // Derive today's open / close / slot-interval from clinic settings.
  // If settings haven't loaded, or the day is closed, use sensible
  // fallbacks so the grid still renders during the loading frame.
  const dayConfig = useMemo(() => {
    const dayKey = DAY_KEYS[clinicDayIndex(clinicDateStringFromInstant(day)) ?? 0];
    const dayHours = settings?.hours?.[dayKey];
    const slotMin = settings?.slot_interval_minutes || FALLBACK_SLOT_MIN;
    if (dayHours?.closed) {
      return { closed: true, openMin: 0, closeMin: 0, slotMin };
    }
    const openMin  = parseHM(dayHours?.open,  FALLBACK_OPEN_MIN);
    const closeMin = parseHM(dayHours?.close, FALLBACK_CLOSE_MIN);
    return { closed: false, openMin, closeMin, slotMin };
  }, [settings, day]);

  const slots = useMemo(
    () => dayConfig.closed ? [] : buildSlots(day, dayConfig.openMin, dayConfig.closeMin, gridZoomMinutes),
    [day, dayConfig, gridZoomMinutes],
  );

  const chairBoard = useMemo(() => buildChairBoard({
    chairs: columns,
    appointments: appts,
    now: new Date(),
    day,
  }), [columns, appts, day]);

  // Today's totals — computed from current appts. Updates with filter/day.
  const totals = useMemo(() => {
    const t = { booked: 0, confirmed: 0, walk_in: 0, completed: 0 };
    for (const a of appts) {
      const status = normalizeStatus(a.status);
      if (["Cancelled","Rescheduled"].includes(status)) continue;
      t.booked += 1;
      if (status === "Confirmed" || status === "Checked In" || status === "Completed") t.confirmed += 1;
      if (status === "Completed") t.completed += 1;
      if (a.walk_in) t.walk_in += 1;
    }
    return t;
  }, [appts]);
  const chairSummary = chairBoard?.summary || {};
  function gotoToday()   { setDay(startOfClinicDay(new Date())); }
  function stepDay(d)    { setDay(addClinicDate(day, d)); }

  function handleSlotClick(start, end, columnChairId) {
    if (needsChairSetup) {
      toast.error("Set up a chair before booking on the calendar.");
      return;
    }
    if (schedulingReadOnly) {
      toast.error("Billing read-only mode blocks new bookings.");
      return;
    }
    const defaultDurationMin = defaultDurationMinutes(settings, dayConfig.slotMin);
    const defaultEnd = new Date(start.getTime() + defaultDurationMin * 60_000);
    setPanel({ kind: "new", start: start.toISOString(), end: defaultEnd.toISOString(), chairId: columnChairId });
  }
  function handleApptClick(a) {
    setPanel({ kind: "detail", appointment: a });
  }
  function handleOpsQueueOpen(appointment) {
    const existing = appts.find((a) => a.id === appointment?.id);
    setPanel({ kind: "detail", appointment: existing || appointment });
  }
  function handleDetailChanged() {
    if (chairsideTutorialDemo) {
      const checkedInAt = new Date().toISOString();
      setAppts((current) => current.map((appointment) => (
        appointment.id === panel?.appointment?.id
          ? { ...appointment, status: "Checked In", checked_in_at: checkedInAt }
          : appointment
      )));
      setAppointmentsRefreshKey((key) => key + 1);
      closePanel();
      return;
    }
    closePanel();
    handleTasksChanged();
  }
  function closePanel() { setPanel(null); }
  async function handleGridZoomChange(nextValue) {
    const next = normalizeCalendarGridZoomMinutes(nextValue);
    const previous = gridZoomMinutes;
    setGridZoomMinutes(next);
    const { data, error: saveError } = await supabase.rpc("update_calendar_user_preferences", {
      p_preferences: { calendar_grid_zoom_minutes: next },
    });
    if (saveError) {
      setGridZoomMinutes(previous);
      toast.error(saveError.message || "Could not save calendar zoom.");
      return;
    }
    if (data?.calendar_grid_zoom_minutes) {
      setGridZoomMinutes(normalizeCalendarGridZoomMinutes(data.calendar_grid_zoom_minutes));
    }
  }

  return (
    <div className="space-y-3">
      {/* Schedule first: keep the booking controls visible and move operational detail behind Tasks. */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="calendar-topbar" data-tutorial-id="calendar-topbar">
        <div className="flex flex-wrap items-center gap-2 px-3 py-2.5">
          <button onClick={() => stepDay(-1)} className="rounded-lg p-2 text-slate-600 hover:bg-slate-100 hover:text-slate-900" aria-label="Previous day" data-testid="calendar-prev">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button onClick={gotoToday} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold shadow-[0_1px_1px_rgba(15,23,42,0.04)] hover:bg-slate-50" data-testid="calendar-today">Today</button>
          <button onClick={() => stepDay(1)} className="rounded-lg p-2 text-slate-600 hover:bg-slate-100 hover:text-slate-900" aria-label="Next day" data-testid="calendar-next">
            <ChevronRight className="w-4 h-4" />
          </button>

          <div className="ml-1 flex min-w-0 items-center gap-2">
            <CalendarIcon className="h-4 w-4 shrink-0 text-slate-500" />
            <span className="hidden truncate text-sm font-semibold text-slate-800 sm:inline">
              {day.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric", timeZone: CLINIC_TIME_ZONE })}
            </span>
          </div>

          <div className="ml-auto flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setFiltersOpen((value) => !value)}
              aria-expanded={filtersOpen}
              aria-controls="calendar-view-filters"
              data-testid="calendar-view-filters-toggle"
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
            >
              <SlidersHorizontal className="h-3.5 w-3.5 text-slate-500" />
              View &amp; filters
            </button>
            <button
              type="button"
              onClick={() => setPanel({ kind: "tasks" })}
              data-testid="calendar-tasks-button"
              aria-label={taskCount === 1 ? "Tasks, 1 item needs attention" : taskCount > 1 ? `Tasks, ${taskCount} items need attention` : "Tasks"}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
            >
              <ListTodo className="h-3.5 w-3.5 text-slate-500" />
              Tasks
              {taskCount > 0 ? (
                <span
                  data-testid="calendar-tasks-badge"
                  className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-rose-600 px-1 text-[10px] font-black tabular-nums text-white"
                >
                  {taskCount > 99 ? "99+" : taskCount}
                </span>
              ) : null}
            </button>
            <button
              onClick={() => setPanel({ kind: "new", start: null, end: null, chairId: null })}
              disabled={schedulingReadOnly || needsChairSetup}
              title={needsChairSetup ? "Set up a chair before booking." : schedulingReadOnly ? "Billing read-only mode blocks new bookings." : "Book appointment"}
              className="inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-teal-700 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-500"
              data-testid="calendar-book-button"
              data-tutorial-id="calendar-book-button">
              <Plus className="h-4 w-4" /> Book appointment
            </button>
          </div>
        </div>

        {filtersOpen ? (
          <div id="calendar-view-filters" className="flex flex-wrap items-center gap-2 border-t border-slate-100 bg-slate-50/70 px-3 py-2.5">
            <div className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white p-1" data-testid="calendar-grid-zoom-control">
              <span className="px-1 text-[10px] font-bold uppercase tracking-wide text-slate-400">Grid</span>
              {CALENDAR_GRID_ZOOM_OPTIONS.map((minutes) => (
                <button
                  key={minutes}
                  type="button"
                  onClick={() => handleGridZoomChange(minutes)}
                  aria-pressed={gridZoomMinutes === minutes}
                  data-testid={`calendar-grid-zoom-${minutes}`}
                  className={[
                    "rounded-md px-2 py-1 text-[11px] font-bold tabular-nums transition",
                    gridZoomMinutes === minutes
                      ? "bg-teal-600 text-white shadow-sm"
                      : "text-slate-600 hover:bg-slate-50",
                  ].join(" ")}
                >
                  {minutes}m
                </button>
              ))}
            </div>
            <Filter className="ml-1 h-4 w-4 text-slate-500" />
            <select value={doctorId} onChange={(e) => setDoctorId(e.target.value)} data-testid="calendar-filter-doctor" data-tutorial-id="calendar-filter-doctor"
              className="max-w-[180px] rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs shadow-[0_1px_1px_rgba(15,23,42,0.03)]">
              <option value="">All doctors</option>
              {doctors.map((d) => <option key={d.id} value={d.id}>{formatDoctorName(d, "Unnamed")}</option>)}
            </select>
            <select value={chairId} onChange={(e) => setChairId(e.target.value)} data-testid="calendar-filter-chair"
              disabled={needsChairSetup}
              className="max-w-[180px] rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs shadow-[0_1px_1px_rgba(15,23,42,0.03)]">
              <option value="">All chairs</option>
              {chairs.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        ) : null}
      </div>

      <CalendarDaySummary totals={totals} chairSummary={chairSummary} loading={loading} />

      {/* Body: left rail + grid */}
      <div className="grid grid-cols-1 lg:grid-cols-[240px_minmax(0,1fr)] gap-3">
        {/* Left rail */}
        <aside className="h-fit rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="calendar-rail">
          <MiniMonthPicker
            value={day}
            onChange={(value) => setDay(startOfClinicDay(value))}
            refreshKey={appointmentsRefreshKey}
            branchId={activeBranchId}
            enabled={Boolean(activeBranchId)}
          />
          <div className="mt-4 border-t border-slate-100 pt-3" data-testid="calendar-totals">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Today&apos;s schedule</p>
            <p className="mt-1.5 text-sm font-semibold text-slate-800">
              {totals.booked === 1 ? "1 appointment" : `${totals.booked} appointments`}
            </p>
            <p className="mt-0.5 text-xs text-slate-500">
              {totals.confirmed === 1 ? "1 confirmed" : `${totals.confirmed} confirmed`}
            </p>
          </div>
        </aside>

        {/* Timeline grid */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          {error ? (
            <div className="p-6 text-sm text-rose-700 bg-rose-50 border-b border-rose-200">{error}</div>
          ) : null}
          {loading ? (
            <div className="p-12 text-center">
              <Loader2 className="w-5 h-5 mx-auto animate-spin text-slate-400" />
              <p className="mt-2 text-xs text-slate-500">Loading day…</p>
            </div>
          ) : needsChairSetup ? (
            <CalendarChairSetupRequired branchName={activeBranch?.name} />
          ) : (
            <div className="overflow-x-auto bg-white" data-testid="calendar-grid" data-tutorial-id="calendar-grid">
              <div
                className="grid"
                style={{
                  gridTemplateColumns: `72px repeat(${columns.length}, minmax(220px, 1fr))`,
                }}
              >
                {/* header row */}
                <div className="border-b border-r border-slate-200 bg-white sticky top-0 z-10" />
                {columns.map((c) => (
                  <div key={c.id || "_all"}
                    className="border-b border-slate-200 bg-white px-3 py-2.5 text-xs font-semibold text-slate-700 sticky top-0 z-10"
                    data-testid={`calendar-col-header-${c.id || "all"}`}>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span>{c.name}</span>
                      {normalizeChairCapabilities(c.chair_capabilities).map((key) => {
                        const meta = getChairCapabilityMeta(key);
                        return (
                          <span
                            key={key}
                            className="rounded bg-teal-50 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wide text-teal-700"
                          >
                            {meta?.shortLabel || key}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                ))}
                {/* slot rows */}
                {slots.map((s) => (
                  <SlotRow key={s.start.toISOString()} slot={s} columns={columns} appts={appts}
                    visualSlotMin={gridZoomMinutes}
                    bookingSlotMin={dayConfig.slotMin}
                    isHourMark={isClinicHourMark(s.start)}
                    onSlotClick={handleSlotClick} onApptClick={handleApptClick}
                    activeApptId={panel?.kind === "detail" ? panel.appointment?.id : null}
                    activeSlotKey={panel?.kind === "new" ? `${panel.start}|${panel.chairId || ""}` : null} />
                ))}
              </div>
              {dayConfig.closed ? (
                <div className="px-6 py-12 text-center" data-testid="calendar-closed-day">
                  <p className="text-sm font-semibold text-slate-700">Closed today</p>
                  <p className="text-xs text-slate-500 mt-1">
                    Edit clinic hours in <Link to="/settings/appointments" className="text-teal-600 hover:underline">Settings → Hours & Booking</Link>.
                  </p>
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>

      {/* Slide-in panel */}
      <SidePanel
        open={!!panel}
        onClose={closePanel}
        title={panel?.kind === "detail" ? "Appointment" : panel?.kind === "tasks" ? "Tasks" : "New appointment"}
      >
        {panel?.kind === "new" ? (
          <NewAppointmentPanel
            doctors={doctors} chairs={chairs} settings={settings}
            initialStart={panel.start} initialEnd={panel.end} initialChairId={panel.chairId}
            tutorialDemo={bookingTutorialDemo}
            onCreated={() => { closePanel(); handleAppointmentsChanged(); }}
            onCancel={closePanel}
          />
        ) : panel?.kind === "detail" ? (
          <AppointmentDetailPanel
            appointment={panel.appointment} doctors={doctors} chairs={chairs}
            settings={settings}
            branchId={activeBranchId}
            readOnly={schedulingReadOnly}
            tutorialDemo={chairsideTutorialDemo}
            onChanged={handleDetailChanged}
            onCancel={closePanel}
          />
        ) : panel?.kind === "tasks" ? (
          <CalendarTasksPanel
            branchId={activeBranchId}
            doctors={doctors}
            chairs={chairs}
            settings={settings}
            readOnly={schedulingReadOnly}
            onOpenAppointment={handleOpsQueueOpen}
            onChanged={handleTasksChanged}
          />
        ) : null}
      </SidePanel>
    </div>
  );
}

function CalendarDaySummary({ totals, chairSummary, loading }) {
  const bookedLabel = totals.booked === 1 ? "1 appointment" : `${totals.booked} appointments`;
  const occupiedLabel = chairSummary.inTreatment === 1 ? "1 chair in use" : `${chairSummary.inTreatment || 0} chairs in use`;
  const readyLabel = chairSummary.ready === 1 ? "1 chair ready" : `${chairSummary.ready || 0} chairs ready`;
  const attentionCount = Number(chairSummary.waiting || 0)
    + Number(chairSummary.checkout || 0)
    + Number(chairSummary.needsTurnover || 0)
    + Number(chairSummary.doctorConflicts || 0);

  return (
    <section
      className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-xs text-slate-600 shadow-sm"
      data-testid="calendar-day-summary"
    >
      <span className="font-semibold text-slate-900">Today</span>
      <span className="text-slate-300" aria-hidden>•</span>
      <span>{loading ? "Loading schedule…" : bookedLabel}</span>
      {loading || chairSummary.inTreatment > 0 ? (
        <>
          <span className="text-slate-300" aria-hidden>•</span>
          <span>{loading ? "Reading chairs…" : occupiedLabel}</span>
        </>
      ) : null}
      {loading || chairSummary.ready > 0 ? (
        <>
          <span className="text-slate-300" aria-hidden>•</span>
          <span>{loading ? "Reading chairs…" : readyLabel}</span>
        </>
      ) : null}
      {!loading && attentionCount > 0 ? (
        <>
          <span className="text-slate-300" aria-hidden>•</span>
          <span className="font-semibold text-amber-800">
            {attentionCount === 1 ? "1 chair needs attention" : `${attentionCount} chairs need attention`}
          </span>
        </>
      ) : null}
    </section>
  );
}

function CalendarChairSetupRequired({ branchName }) {
  return (
    <div
      className="flex min-h-[420px] items-center justify-center bg-slate-50/70 px-6 py-12"
      data-testid="calendar-chair-setup-required"
    >
      <div className="max-w-md text-center">
        <span className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-lg bg-teal-50 text-teal-700">
          <Armchair className="h-6 w-6" strokeWidth={2.25} />
        </span>
        <h2 className="mt-4 text-lg font-bold text-slate-950">Set up chairs first</h2>
        <p className="mt-2 text-sm leading-6 text-slate-600">
          Calendar scheduling is unavailable until {branchName || "this branch"} has at least one active chair.
        </p>
        <Link
          to="/settings/chairs"
          className="mt-5 inline-flex items-center justify-center rounded-md bg-teal-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-teal-700"
          data-testid="calendar-chair-settings-link"
        >
          Open chair settings
        </Link>
      </div>
    </div>
  );
}

// ---- SlotRow ---------------------------------------------------------
function SlotRow({ slot, columns, appts, visualSlotMin, bookingSlotMin, isHourMark, onSlotClick, onApptClick, activeApptId, activeSlotKey }) {
  const isBookableStart = isInstantAlignedToSlotInterval(slot.start, bookingSlotMin);
  const rowHeightPx = calendarSlotHeightPx(visualSlotMin);
  return (
    <>
      <div className={`px-2 py-1 text-[10px] text-slate-400 text-right border-r border-b border-slate-100 bg-slate-50/70 ${isHourMark ? "border-t border-slate-200 text-slate-500" : ""}`}>
        {isHourMark ? fmtHM(slot.start) : ""}
      </div>
      {columns.map((c) => {
        const startsHere = appts.filter((a) =>
          (a.chair_id || null) === (c.id || null) &&
          isVisibleCalendarStatus(a.status) &&
          inSlot(new Date(a.scheduled_start), slot.start, slot.end),
        );
        const slotKey = `${slot.start.toISOString()}|${c.id || ""}`;
        const isActiveSlot = activeSlotKey === slotKey && startsHere.length === 0;
        return (
          <div
            key={c.id || "_all"}
            className={`relative overflow-visible border-b border-slate-100 ${isBookableStart ? "cursor-pointer hover:bg-slate-50/70" : "cursor-default bg-slate-50/30"} ${isHourMark ? "border-t border-slate-200" : ""} ${isActiveSlot ? "bg-teal-50/60" : ""}`}
            style={{ minHeight: `${rowHeightPx}px` }}
            onClick={(e) => {
              if (e.target.closest("[data-appt-chip]")) return;
              if (!isBookableStart) return;
              onSlotClick(slot.start, slot.end, c.id || null);
            }}
            data-testid={`calendar-slot-${c.id || "all"}-${slot.start.toISOString()}`}
          >
            {startsHere.map((a) => (
              <ApptChip key={a.id} appt={a} active={a.id === activeApptId}
                slotStart={slot.start}
                slotMin={visualSlotMin}
                slotHeightPx={rowHeightPx}
                onClick={() => onApptClick(a)} />
            ))}
          </div>
        );
      })}
    </>
  );
}

function ApptChip({ appt, active, onClick, slotStart, slotMin = FALLBACK_SLOT_MIN, slotHeightPx = SLOT_PX }) {
  const start = new Date(appt.scheduled_start);
  const end   = new Date(appt.scheduled_end);
  const safeSlotMin = normalizeSlotIntervalMinutes(slotMin);
  const minutes = Math.max(1, (end - start) / 60000);
  const offsetMinutes = Math.max(0, (start - new Date(slotStart)) / 60000);
  const topPx = Math.max(2, (offsetMinutes / safeSlotMin) * slotHeightPx + 2);
  const heightPx = Math.max(MIN_APPOINTMENT_CHIP_PX, (minutes / safeSlotMin) * slotHeightPx - 4);
  const meta = getStatusMeta(appt.status);
  const compact = heightPx < 42;
  const noShowCount = Number(appt.patient_no_show_count || 0);
  const normalized = normalizeStatus(appt.status);
  // Stacking order on overlapping slots: finished/inactive appointments
  // (Completed / Cancelled / No Show) sit BEHIND live ones (Scheduled,
  // Confirmed, Checked In, Pencil) so an active visit is never hidden behind
  // a done one. Hover/selection still lift any chip to the front (z-30).
  const baseZ = ["Completed", "Cancelled", "No Show"].includes(normalized) ? "z-10" : "z-20";
  const procedureLines = buildAppointmentProcedureLines(appt);
  const riskLabels = normalizeCalendarOpsRiskLabels(appt.calendar_ops_risk_labels);
  // Lateness signal: scheduled start has passed, patient was supposed to be
  // here, but they never checked in and weren't cancelled or no-showed yet.
  const now = Date.now();
  const isLate = start.getTime() < now
    && (normalized === "Scheduled" || normalized === "Confirmed")
    && !appt.checked_in_at
    && !appt.cancelled_at;
  return (
    <button
      type="button"
      data-appt-chip
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      data-testid={`calendar-appt-${appt.id}`}
      data-tutorial-id={appt.tutorial_id || undefined}
      style={{ height: `${heightPx}px`, top: `${topPx}px` }}
      className={`group absolute left-1 right-1 ${baseZ} flex items-start justify-start ${meta.eventClass} border rounded-lg px-2 py-1 text-left shadow-[0_4px_10px_rgba(15,23,42,0.08)] hover:z-30 hover:shadow-md transition overflow-hidden ${active ? "z-30 ring-2 ring-teal-500 ring-offset-1" : ""} ${isLate ? "ring-2 ring-rose-400 ring-offset-1 animate-pulse" : ""}`}
      title={isLate ? "Late — patient hasn't checked in past scheduled start" : undefined}
    >
      <span aria-hidden className={`absolute left-0 top-0 h-full w-1 ${meta.accentClass}`} />
      <div className="pl-1 min-w-0">
        <div className="flex items-start justify-between gap-2 min-w-0">
          <div className="min-w-0">
            <div className="font-semibold truncate text-[11px] leading-4 flex items-center gap-1">
              <span className="truncate">{appt.patient_name || "—"}{appt.walk_in ? " · walk-in" : ""}</span>
              {noShowCount > 0 ? (
                <span
                  className="inline-flex items-center justify-center shrink-0 rounded-full bg-rose-200 px-1.5 text-[9px] font-bold leading-[1.4] text-rose-900"
                  title={`${noShowCount} prior no-show${noShowCount === 1 ? "" : "s"} for this patient`}
                  data-testid={`appt-noshow-history-${appt.id}`}
                >
                  ⚠ {noShowCount}
                </span>
              ) : null}
            </div>
            <div className="text-[10px] opacity-70 truncate leading-3.5">
              {fmtHM(start)}–{fmtHM(end)} · {formatDoctorName(appt.doctor_name, "Unassigned")}
              {isLate ? <span className="ml-1 font-semibold text-rose-700">· LATE</span> : null}
            </div>
          </div>
          <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide leading-none ${meta.pillClass}`}>
            {meta.label}
          </span>
        </div>
      {procedureLines.length > 0 && !compact ? (
        <div
          className="mt-0.5 space-y-0.5 pl-0 text-[10px] font-medium leading-3.5 opacity-85"
          title={procedureLines.join("\n")}
        >
          {procedureLines.map((line, index) => (
            <div key={`${line}-${index}`} className="truncate">
              {line}
            </div>
          ))}
        </div>
      ) : null}
      {riskLabels.length > 0 && !compact ? (
        <div className="mt-1 flex flex-wrap gap-1" data-testid={`calendar-ops-risk-labels-${appt.id}`}>
          {riskLabels.slice(0, 3).map((label) => (
            <span
              key={`${label.type}-${label.label}`}
              className={`rounded px-1.5 py-0.5 text-[9px] font-black uppercase leading-none ${riskLabelClass(label.severity)}`}
              title={label.detail || label.label}
            >
              {label.label}
            </span>
          ))}
        </div>
      ) : null}
      {normalized === "Pencil" && !compact ? (
        <div className="text-[9px] italic opacity-70 truncate" title="This slot is reserved by an in-flight checkout. It commits when the receptionist closes the visit, or releases if they discard.">
          tentative — not yet committed
        </div>
      ) : null}
      </div>
    </button>
  );
}

// ---- helpers ---------------------------------------------------------
function normalizeStatus(status) {
  return normalizeAppointmentStatus(status);
}
function getStatusMeta(status) {
  return STATUS_META[normalizeStatus(status)] || STATUS_META.Scheduled;
}
function startOfClinicDay(value) {
  return clinicDateAt(clinicDateStringFromInstant(value) || clinicTodayDateString(), 0, 0, 0);
}
function addClinicDate(value, days) {
  return clinicDateAt(addClinicDays(clinicDateStringFromInstant(value), days), 0, 0, 0);
}
function fmtHM(d)      { return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", timeZone: CLINIC_TIME_ZONE }); }
function calendarSlotHeightPx(visualSlotMin) {
  const safeSlotMin = normalizeCalendarGridZoomMinutes(visualSlotMin);
  return Math.max(SLOT_PX, (safeSlotMin / 60) * GRID_HOUR_TARGET_PX);
}
function buildChairsideTutorialDemoData(day) {
  const date = clinicDateStringFromInstant(day) || clinicTodayDateString();
  const startMin = 11 * 60;
  const start = clinicDateAtMinutes(date, startMin);
  const end = clinicDateAtMinutes(date, startMin + TUTORIAL_APPOINTMENT_MINUTES);
  const hours = Object.fromEntries(DAY_KEYS.map((key) => [key, { open: "09:00", close: "17:00", closed: false }]));
  return {
    appointments: [{
      id: "tutorial-chairside-checkin",
      tutorial_id: "calendar-chairside-demo-appointment",
      patient_id: "tutorial-patient",
      patient_name: "Juan Dela Cruz",
      patient_phone: "09171234567",
      patient_email: "juan@example.test",
      doctor_id: "tutorial-doctor",
      doctor_name: "Dr. Levi Jordan",
      chair_id: "tutorial-chair-1",
      chair_name: "Chair 1",
      scheduled_start: start.toISOString(),
      scheduled_end: end.toISOString(),
      status: "Confirmed",
      checked_in_at: null,
      cancelled_at: null,
      walk_in: false,
      procedure_name: "Comprehensive Oral Exam",
      procedures: [{ name: "Comprehensive Oral Exam" }],
      calendar_ops_risk_labels: [],
    }],
    doctors: [{ id: "tutorial-doctor", display_name: "Dr. Levi Jordan", full_name: "Dr. Levi Jordan" }],
    chairs: [{ id: "tutorial-chair-1", name: "Chair 1", chair_capabilities: [] }],
    settings: { slot_interval_minutes: TUTORIAL_APPOINTMENT_MINUTES, hours },
  };
}
function buildSlots(day, openMin, closeMin, slotMin) {
  const out = [];
  const date = clinicDateStringFromInstant(day);
  for (let m = openMin; m < closeMin; m += slotMin) {
    const s = clinicDateAtMinutes(date, m);
    const e = clinicDateAtMinutes(date, m + slotMin);
    out.push({ start: s, end: e });
  }
  return out;
}
function isClinicHourMark(value) {
  return clinicTimePartsFromInstant(value)?.minute === 0;
}
function isInstantAlignedToSlotInterval(value, slotIntervalMinutes) {
  const parts = clinicTimePartsFromInstant(value);
  if (!parts) return false;
  const interval = normalizeSlotIntervalMinutes(slotIntervalMinutes);
  return ((Number(parts.hour || 0) * 60) + Number(parts.minute || 0)) % interval === 0;
}
function inSlot(t, slotStart, slotEnd) { return t >= slotStart && t < slotEnd; }
function parseHM(str, fallback) {
  if (!str || typeof str !== "string") return fallback;
  const m = str.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return fallback;
  return Number(m[1]) * 60 + Number(m[2]);
}
function attachCalendarOpsRisks(appointments, payload) {
  const rows = Array.isArray(payload?.appointments) ? payload.appointments : [];
  const labelsByAppointment = new Map(rows.map((row) => [row.appointment_id, row.risk_labels || []]));
  return appointments.map((appointment) => ({
    ...appointment,
    calendar_ops_risk_labels: labelsByAppointment.get(appointment.id) || appointment.calendar_ops_risk_labels || [],
  }));
}
function normalizeCalendarOpsRiskLabels(value) {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => {
      if (typeof item === "string") return { type: item, label: humanizeRiskType(item), severity: "info" };
      return {
        type: item?.type || item?.key || item?.label || "risk",
        label: item?.label || humanizeRiskType(item?.type || item?.key || "Risk"),
        severity: item?.severity || "info",
        detail: item?.detail || null,
      };
    })
    .filter((item) => item.label);
}
function humanizeRiskType(value) {
  return String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}
function riskLabelClass(severity) {
  if (severity === "critical") return "bg-rose-100 text-rose-800";
  if (severity === "warning") return "bg-amber-100 text-amber-800";
  return "bg-slate-100 text-slate-700";
}
